import { useEffect, useMemo, useRef, useState } from "react";
import { Heart, PartyPopper, Eye } from "lucide-react";
import { LOGO_DARK_URL } from "@/lib/checkout";
import { SiteFooter } from "@/components/SiteFooter";
import { apiBase } from "@/lib/native";

const API = `${apiBase()}/api`;
const BACKEND = apiBase();
import { MobileTabBar } from "@/components/MobileTabBar";

const AVATAR_COLORS = ["#3A7CA5", "#5FB89B", "#F5A623", "#2B3B52", "#8B93A0", "#4F8068"];
function colorFor(name) {
  let h = 0;
  for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
}
function initialsAvatar(name) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return { initials, color: colorFor(name) };
}
const tagClass = (tag) => (tag || "").replace(" ", "-");

const CSS = `
.cc-root{
  --bg:#F6F8FA; --panel:#FFFFFF; --panel-2:#F6F8FA;
  --line:#E6E9EE; --line-strong:#D9DEE6;
  --ink:#0B132B; --ink-muted:#5A6472; --faint:#8A94A3;
  --gold:#F5A623; --gold-soft:rgba(245,166,35,0.14);
  --teal:#15816B; --teal-soft:rgba(21,129,107,0.12);
  --blue:#A8D5F5; --blue-soft:rgba(168,213,245,0.30);
  --green:#15816B; --green-soft:rgba(21,129,107,0.12);
  --coral:#B4443E; --soft-fill:#F1F4F8;
  --radius:14px; --shadow:0 1px 2px rgba(11,19,43,0.06), 0 10px 30px rgba(11,19,43,0.08);
  background:var(--bg); color:var(--ink); font-family:'Manrope',sans-serif; font-size:15px; line-height:1.6;
  -webkit-font-smoothing:antialiased; min-height:100vh;
}
.cc-root *{ box-sizing:border-box; }
.cc-root h1,.cc-root h2,.cc-root h3,.cc-root .display{ font-family:'Outfit',sans-serif; color:#fff; margin:0; font-weight:700; }
.cc-root a{ color:inherit; }
.cc-root button{ font-family:inherit; cursor:pointer; }
.cc-root :focus-visible{ outline:2px solid var(--gold); outline-offset:2px; }
.cc-root .eyebrow{ font-family:'Outfit',sans-serif; text-transform:uppercase; letter-spacing:2px; font-size:11px; font-weight:700; color:var(--gold); }
.cc-root .wrap{ max-width:1120px; margin:0 auto; padding:0 24px; }
.cc-root .nav{ position:sticky; top:0; z-index:40; background:rgba(11,19,43,0.85); backdrop-filter:blur(10px); border-bottom:1px solid var(--line); }
.cc-root .nav-inner{ display:flex; align-items:center; justify-content:space-between; gap:16px; padding:12px 24px; max-width:1120px; margin:0 auto; }
.cc-root .nav-links{ display:flex; gap:26px; font-family:'Outfit',sans-serif; font-size:13.5px; font-weight:500; color:var(--ink-muted); align-items:center; }
.cc-root .nav-links a{ text-decoration:none; } .cc-root .nav-links a:hover{ color:#fff; }
.cc-root .btn{ font-family:'Outfit',sans-serif; font-weight:700; font-size:13px; border-radius:999px; border:none; padding:10px 20px; letter-spacing:0.2px; transition:transform .12s ease, box-shadow .12s ease, background .15s ease, border-color .15s ease; }
.cc-root .btn:active{ transform:scale(0.97); }
.cc-root .btn-primary{ background:var(--gold); color:#0B132B; } .cc-root .btn-primary:hover{ background:#ffb733; }
.cc-root .btn-ghost{ background:transparent; color:#E6EDF6; border:1.5px solid var(--line-strong); } .cc-root .btn-ghost:hover{ border-color:var(--gold); color:#fff; }
.cc-root .btn-teal{ background:var(--gold); color:#0B132B; } .cc-root .btn-teal:hover{ background:#ffb733; }
.cc-root .btn-sm{ padding:8px 15px; font-size:12px; }
.cc-root .hero{ padding:64px 0 40px; }
.cc-root .hero-grid{ display:grid; grid-template-columns:1.05fr 0.95fr; gap:56px; align-items:center; }
.cc-root .hero h1{ font-size:44px; line-height:1.12; margin:14px 0 18px; font-weight:800; }
.cc-root .hero h1 em{ font-style:normal; color:var(--gold); }
.cc-root .hero p{ color:var(--ink-muted); font-size:16px; max-width:470px; margin-bottom:26px; }
.cc-root .hero-ctas{ display:flex; gap:12px; align-items:center; flex-wrap:wrap; }
.cc-root .hero-meta{ margin-top:24px; font-size:12.5px; color:var(--faint); display:flex; gap:10px; align-items:center; }
.cc-root .avatar-stack{ display:flex; }
.cc-root .avatar-stack .mini{ width:26px; height:26px; border-radius:50%; border:2px solid var(--bg); margin-left:-8px; display:inline-flex; align-items:center; justify-content:center; color:#fff; font-family:'Outfit',sans-serif; font-size:9px; font-weight:700; }
.cc-root .avatar-stack .mini:first-child{ margin-left:0; }
.cc-root .peek{ background:var(--panel); border-radius:18px; box-shadow:var(--shadow); border:1px solid var(--line); padding:20px; }
.cc-root .content{ display:grid; grid-template-columns:1fr 320px; gap:32px; padding:20px 0 80px; align-items:start; }
.cc-root .card{ background:var(--panel); border:1px solid var(--line); border-radius:var(--radius); box-shadow:var(--shadow); }
.cc-root .composer{ padding:16px 18px; margin-bottom:18px; }
.cc-root .composer-top{ display:flex; gap:12px; align-items:flex-start; }
.cc-root .avatar{ width:40px; height:40px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-family:'Outfit',sans-serif; font-weight:700; font-size:13px; color:#fff; }
.cc-root .composer textarea{ flex:1; border:1.5px solid var(--line-strong); border-radius:12px; padding:11px 14px; font-family:'Manrope',sans-serif; font-size:14px; resize:none; min-height:44px; background:rgba(255,255,255,0.04); color:var(--ink); transition:border-color .15s ease, background .15s ease; }
.cc-root .composer textarea::placeholder{ color:var(--faint); }
.cc-root .composer textarea:focus{ border-color:var(--gold); background:rgba(255,255,255,0.06); outline:none; }
.cc-root .composer-tags{ display:flex; gap:8px; margin-top:12px; flex-wrap:wrap; }
.cc-root .chip{ font-family:'Outfit',sans-serif; font-size:11.5px; font-weight:500; border:1.5px solid var(--line-strong); background:transparent; color:var(--ink-muted); padding:6px 13px; border-radius:999px; transition:all .12s ease; }
.cc-root .chip[data-active="true"]{ border-color:transparent; color:#0B132B; }
.cc-root .chip[data-tag="Win"][data-active="true"]{ background:var(--gold); }
.cc-root .chip[data-tag="Question"][data-active="true"]{ background:var(--blue); color:#fff; }
.cc-root .chip[data-tag="Check in"][data-active="true"]{ background:var(--green); color:#0B132B; }
.cc-root .chip[data-tag="Just venting"][data-active="true"]{ background:#3A4A63; color:#fff; }
.cc-root .composer-actions{ display:flex; justify-content:space-between; align-items:center; margin-top:12px; }
.cc-root .composer-signin{ display:flex; gap:12px; align-items:center; justify-content:space-between; flex-wrap:wrap; }
.cc-root .composer-signin p{ margin:0; color:var(--ink-muted); font-size:13.5px; }
.cc-root .filterbar{ display:flex; gap:8px; margin:0 0 16px 2px; flex-wrap:wrap; }
.cc-root .filterbar button{ background:transparent; border:none; font-family:'Outfit',sans-serif; font-size:12.5px; font-weight:500; color:var(--ink-muted); padding:7px 3px; border-bottom:2px solid transparent; }
.cc-root .filterbar button[data-active="true"]{ color:#fff; border-bottom-color:var(--gold); font-weight:700; }
.cc-root .post{ padding:18px 18px 8px; margin-bottom:16px; }
.cc-root .post.pinned{ border-color:rgba(245,166,35,0.4); background:linear-gradient(180deg,rgba(245,166,35,0.06),var(--panel)); }
.cc-root .post-head{ display:flex; gap:11px; align-items:flex-start; }
.cc-root .post-meta{ flex:1; }
.cc-root .post-name{ font-family:'Outfit',sans-serif; font-weight:700; font-size:13.5px; color:#fff; display:flex; align-items:center; gap:6px; }
.cc-root .badge{ font-family:'Outfit',sans-serif; font-size:9.5px; font-weight:700; letter-spacing:.4px; background:var(--gold-soft); color:var(--gold); padding:2px 7px; border-radius:6px; text-transform:uppercase; }
.cc-root .post-sub{ font-size:11.5px; color:var(--faint); margin-top:1px; }
.cc-root .post-tag{ font-family:'Outfit',sans-serif; font-size:10px; font-weight:700; letter-spacing:.3px; padding:3px 9px; border-radius:999px; text-transform:uppercase; white-space:nowrap; }
.cc-root .post-tag.Win{ background:var(--gold-soft); color:#F5C976; }
.cc-root .post-tag.Question{ background:var(--blue-soft); color:#9CC6E2; }
.cc-root .post-tag.Check-in{ background:var(--green-soft); color:#8BD3BC; }
.cc-root .post-tag.Just-venting{ background:rgba(255,255,255,0.08); color:var(--ink-muted); }
.cc-root .post-body{ font-size:14.5px; color:var(--ink); margin:12px 0 14px; line-height:1.65; white-space:pre-wrap; }
.cc-root .post-actions{ display:flex; gap:6px; border-top:1px solid var(--line); padding:10px 0; margin-top:2px; flex-wrap:wrap; }
.cc-root .reaction{ display:flex; align-items:center; gap:6px; background:transparent; border:1.5px solid var(--line-strong); border-radius:999px; padding:6px 12px; font-size:12px; font-weight:600; color:var(--ink-muted); transition:all .12s ease; }
.cc-root .reaction .dot{ width:7px; height:7px; border-radius:50%; }
.cc-root .reaction[data-kind="same"] .dot{ background:var(--gold); }
.cc-root .reaction[data-kind="cheer"] .dot{ background:var(--blue); }
.cc-root .reaction[data-kind="seen"] .dot{ background:var(--green); }
.cc-root .reaction[data-active="true"]{ background:var(--gold-soft); border-color:transparent; color:#fff; font-weight:700; }
.cc-root .reaction:hover{ border-color:var(--gold); }
.cc-root .comment-toggle{ margin-left:auto; background:transparent; border:none; font-size:12px; color:var(--faint); font-family:'Outfit',sans-serif; font-weight:500; }
.cc-root .comment-toggle:hover{ color:#fff; }
.cc-root .comments{ border-top:1px solid var(--line); padding:12px 0 6px; }
.cc-root .comment{ display:flex; gap:9px; margin-bottom:10px; align-items:flex-start; }
.cc-root .comment .avatar{ width:28px; height:28px; font-size:10.5px; }
.cc-root .comment-bubble{ background:rgba(255,255,255,0.05); border-radius:12px; padding:8px 12px; font-size:13px; color:var(--ink); }
.cc-root .comment-bubble .cname{ font-family:'Outfit',sans-serif; font-weight:700; font-size:12px; color:#fff; display:block; margin-bottom:1px; }
.cc-root .comment-input-row{ display:flex; gap:9px; align-items:center; margin-top:8px; }
.cc-root .comment-input-row input{ flex:1; border:1.5px solid var(--line-strong); border-radius:999px; padding:8px 14px; font-size:13px; font-family:'Manrope',sans-serif; background:rgba(255,255,255,0.04); color:var(--ink); }
.cc-root .comment-input-row input::placeholder{ color:var(--faint); }
.cc-root .comment-input-row input:focus{ outline:none; border-color:var(--gold); background:rgba(255,255,255,0.06); }
.cc-root .side-card{ padding:18px; margin-bottom:18px; }
.cc-root .side-card h3{ font-size:14.5px; margin-bottom:10px; color:#fff; }
.cc-root .prompt-card{ background:var(--gold-soft); border:1px solid rgba(245,166,35,0.25); }
.cc-root .prompt-card .eyebrow{ display:block; margin-bottom:8px; }
.cc-root .prompt-card .pq{ font-family:'Outfit',sans-serif; font-weight:700; color:#fff; font-size:15.5px; margin:0 0 6px; }
.cc-root .prompt-card .pb{ color:#E9D3A6; font-size:13.5px; margin:0; }
.cc-root .guideline-list{ list-style:none; margin:0; padding:0; }
.cc-root .guideline-list li{ display:flex; gap:9px; font-size:12.8px; color:var(--ink-muted); margin-bottom:10px; align-items:flex-start; }
.cc-root .guideline-list li .dot{ width:6px; height:6px; border-radius:50%; background:var(--gold); margin-top:6px; flex-shrink:0; }
.cc-root .room-row{ display:flex; justify-content:space-between; align-items:center; padding:9px 0; border-bottom:1px solid var(--line); }
.cc-root .room-row:last-of-type{ border-bottom:none; }
.cc-root .room-row .rday{ font-family:'Outfit',sans-serif; font-weight:700; font-size:12.5px; color:#fff; }
.cc-root .room-row .rtime{ font-size:11.5px; color:var(--faint); }
.cc-root .spotlight-quote{ color:var(--ink); font-size:13.5px; margin:8px 0; line-height:1.6; }
.cc-root .spotlight-name{ font-family:'Outfit',sans-serif; font-size:12px; font-weight:700; color:var(--gold); }
.cc-root .side-links{ display:flex; flex-direction:column; gap:9px; }
.cc-root .side-links a{ text-decoration:none; font-family:'Outfit',sans-serif; font-size:12.5px; font-weight:600; color:#E6EDF6; display:flex; justify-content:space-between; align-items:center; padding:10px 12px; background:rgba(255,255,255,0.04); border-radius:9px; transition:background .15s ease, color .15s ease; }
.cc-root .side-links a:hover{ background:var(--gold-soft); color:var(--gold); }
.cc-root .join-section{ background:var(--panel-2); border-top:1px solid var(--line); padding:70px 0; }
.cc-root .join-inner{ text-align:center; max-width:560px; margin:0 auto; }
.cc-root .join-inner .eyebrow{ color:var(--gold); }
.cc-root .join-inner h2{ color:#fff; font-size:34px; margin:14px 0 12px; font-weight:800; }
.cc-root .join-inner p{ color:var(--ink-muted); font-size:15px; margin-bottom:28px; }
.cc-root .join-form{ display:flex; gap:10px; justify-content:center; flex-wrap:wrap; }
.cc-root .join-form input{ border:1.5px solid var(--line-strong); border-radius:999px; padding:13px 18px; font-size:14px; width:280px; font-family:'Manrope',sans-serif; background:rgba(255,255,255,0.04); color:var(--ink); }
.cc-root .join-form input::placeholder{ color:var(--faint); }
.cc-root .join-form input:focus{ outline:2px solid var(--gold); }
.cc-root .join-note{ color:var(--faint); font-size:11.5px; margin-top:14px; }
.cc-root .join-confirm{ color:#fff; font-family:'Outfit',sans-serif; font-size:16px; }
.cc-root .foot-brand{ padding:34px 0; text-align:center; border-top:1px solid var(--line); }
.cc-root .foot-brand .foot-logo{ display:inline-flex; justify-content:center; margin-bottom:12px; }
.cc-root .foot-brand p{ color:var(--faint); font-size:11.5px; }
.cc-root .cbadge{ font-family:'Outfit',sans-serif; font-size:9px; font-weight:700; letter-spacing:.3px; padding:2px 7px; border-radius:6px; text-transform:uppercase; background:var(--blue-soft); color:#9CC6E2; }
.cc-root .cbadge[data-badge="Top Contributor"], .cc-root .cbadge[data-badge="Team"]{ background:var(--gold-soft); color:var(--gold); }
.cc-root .cbadge[data-badge="Active Contributor"]{ background:var(--green-soft); color:#8BD3BC; }
.cc-root img.avatar{ object-fit:cover; }
.cc-root .name-btn{ background:none; border:none; padding:0; color:inherit; font:inherit; cursor:pointer; }
.cc-root .name-btn:hover{ text-decoration:underline; }
.cc-root .post-img{ margin:4px 0 14px; border-radius:12px; border:1px solid var(--line); max-width:100%; max-height:420px; display:block; }
.cc-root .link-btn{ background:none; border:none; color:var(--faint); font-family:'Outfit',sans-serif; font-size:11.5px; font-weight:600; cursor:pointer; padding:2px 0; }
.cc-root .link-btn:hover{ color:#fff; }
.cc-root .reply{ display:flex; gap:8px; margin:0 0 10px 30px; align-items:flex-start; }
.cc-root .reply .avatar{ width:24px; height:24px; font-size:9.5px; }
.cc-root .reply-input-row{ display:flex; gap:8px; align-items:center; margin:0 0 8px 30px; }
.cc-root .reply-input-row input{ flex:1; border:1.5px solid var(--line-strong); border-radius:999px; padding:6px 12px; font-size:12.5px; font-family:'Manrope',sans-serif; background:rgba(255,255,255,0.04); color:var(--ink); }
.cc-root .reply-input-row input:focus{ outline:none; border-color:var(--gold); }
.cc-root .img-btn{ display:inline-flex; align-items:center; gap:6px; background:transparent; border:1.5px solid var(--line-strong); color:var(--ink-muted); border-radius:999px; padding:8px 14px; font-size:12px; font-weight:600; font-family:'Outfit',sans-serif; cursor:pointer; }
.cc-root .img-btn:hover{ border-color:var(--gold); color:#fff; }
.cc-root .img-preview{ position:relative; margin-top:12px; display:inline-block; }
.cc-root .img-preview img{ max-height:160px; border-radius:10px; border:1px solid var(--line); display:block; }
.cc-root .img-preview .rm{ position:absolute; top:6px; right:6px; background:rgba(0,0,0,0.65); color:#fff; border:none; border-radius:50%; width:26px; height:26px; cursor:pointer; font-size:15px; line-height:1; }
.cc-root .modal-overlay{ position:fixed; inset:0; z-index:200; background:rgba(3,8,20,0.72); display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(4px); }
.cc-root .modal-card{ background:var(--panel); border:1px solid var(--line-strong); border-radius:18px; max-width:440px; width:100%; padding:26px; box-shadow:var(--shadow); }
.cc-root .modal-card h3{ font-size:19px; margin:0; }
.cc-root .modal-card .mclose{ position:absolute; }
.cc-root .modal-card textarea, .cc-root .modal-card input[type=text]{ width:100%; border:1.5px solid var(--line-strong); border-radius:10px; padding:10px 12px; font-family:'Manrope',sans-serif; font-size:14px; background:rgba(255,255,255,0.04); color:var(--ink); }
.cc-root .modal-card textarea:focus, .cc-root .modal-card input:focus{ outline:none; border-color:var(--gold); }
.cc-root .field-label{ display:block; font-family:'Outfit',sans-serif; font-size:12px; font-weight:600; color:var(--ink-muted); margin:14px 0 6px; }
.cc-root .nav-msg-btn{ position:relative; }
.cc-root .nav-msg-btn .dm-unread{ position:absolute; top:-6px; right:-6px; }
.cc-root .dm-unread{ background:var(--gold); color:#0B132B; border-radius:999px; font-size:10px; font-weight:800; min-width:18px; height:18px; display:inline-flex; align-items:center; justify-content:center; padding:0 5px; font-family:'Outfit',sans-serif; }
.cc-root .dm-overlay{ position:fixed; inset:0; z-index:210; background:rgba(3,8,20,0.72); display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(4px); }
.cc-root .dm-card{ background:var(--panel); border:1px solid var(--line-strong); border-radius:18px; width:100%; max-width:820px; height:min(80vh,620px); display:flex; overflow:hidden; box-shadow:var(--shadow); }
.cc-root .dm-list{ width:290px; border-right:1px solid var(--line); display:flex; flex-direction:column; min-height:0; }
.cc-root .dm-list-head{ padding:16px 18px; border-bottom:1px solid var(--line); display:flex; justify-content:space-between; align-items:center; }
.cc-root .dm-conv{ display:flex; gap:10px; align-items:center; padding:12px 16px; cursor:pointer; border-bottom:1px solid var(--line); }
.cc-root .dm-conv:hover{ background:rgba(255,255,255,0.03); }
.cc-root .dm-conv[data-active="true"]{ background:rgba(245,166,35,0.08); }
.cc-root .dm-conv .meta{ flex:1; min-width:0; }
.cc-root .dm-conv .nm{ font-family:'Outfit',sans-serif; font-weight:700; font-size:13.5px; }
.cc-root .dm-conv .lm{ font-size:12px; color:var(--faint); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cc-root .dm-thread{ flex:1; display:flex; flex-direction:column; min-width:0; }
.cc-root .dm-thread-head{ padding:14px 18px; border-bottom:1px solid var(--line); display:flex; gap:10px; align-items:center; }
.cc-root .dm-back{ display:none; }
.cc-root .dm-msgs{ flex:1; overflow-y:auto; padding:18px; display:flex; flex-direction:column; gap:8px; }
.cc-root .dm-bubble{ max-width:75%; padding:9px 13px; border-radius:14px; font-size:14px; line-height:1.45; word-wrap:break-word; }
.cc-root .dm-bubble.mine{ align-self:flex-end; background:var(--teal); color:#04211B; border-bottom-right-radius:4px; }
.cc-root .dm-bubble.theirs{ align-self:flex-start; background:rgba(255,255,255,0.06); color:var(--ink); border-bottom-left-radius:4px; }
.cc-root .dm-input{ display:flex; gap:10px; padding:14px; border-top:1px solid var(--line); }
.cc-root .dm-input input{ flex:1; border:1.5px solid var(--line-strong); border-radius:999px; padding:10px 16px; background:rgba(255,255,255,0.04); color:var(--ink); font-family:'Manrope',sans-serif; font-size:14px; }
.cc-root .dm-input input:focus{ outline:none; border-color:var(--gold); }
.cc-root .dm-empty{ flex:1; display:flex; align-items:center; justify-content:center; color:var(--faint); font-size:13px; padding:24px; text-align:center; line-height:1.5; }
@media(max-width:640px){
  .cc-root .dm-card{ height:88vh; max-width:100%; }
  .cc-root .dm-list{ width:100%; }
  .cc-root .dm-card[data-mobile-view="thread"] .dm-list{ display:none; }
  .cc-root .dm-card[data-mobile-view="list"] .dm-thread{ display:none; }
  .cc-root .dm-back{ display:inline-block; }
}
@media (max-width:860px){
  .cc-root .hero-grid{ grid-template-columns:1fr; }
  .cc-root .content{ grid-template-columns:1fr; }
  .cc-root .nav-links{ display:none; }
  .cc-root .hero h1{ font-size:33px; }
}

/* ===== Light theme overrides (align to landing palette) ===== */
.cc-root h1, .cc-root h2, .cc-root h3, .cc-root .display{ color:var(--ink); }
.cc-root .modal-overlay{ z-index:230; }
.cc-root .nav{ background:rgba(255,255,255,0.88); }
.cc-root :focus-visible{ outline-color:var(--teal); }
.cc-root .eyebrow{ color:var(--teal); }
.cc-root .hero h1 em{ color:var(--teal); }
.cc-root .btn-primary{ background:var(--ink); color:#fff; } .cc-root .btn-primary:hover{ background:#1b2748; }
.cc-root .btn-teal{ background:var(--ink); color:#fff; } .cc-root .btn-teal:hover{ background:#1b2748; }
.cc-root .btn-ghost{ color:var(--ink); border-color:var(--line-strong); } .cc-root .btn-ghost:hover{ border-color:var(--ink); color:var(--ink); }
.cc-root .composer textarea{ background:var(--soft-fill); } .cc-root .composer textarea:focus{ border-color:var(--teal); background:#fff; }
.cc-root .chip[data-tag][data-active="true"]{ background:var(--teal); color:#fff; border-color:var(--teal); }
.cc-root .filterbar button[data-active="true"]{ color:var(--ink); border-bottom-color:var(--teal); }
.cc-root .post.pinned{ border-color:rgba(21,129,107,0.35); background:linear-gradient(180deg,rgba(21,129,107,0.06),#fff); }
.cc-root .post-tag.Just-venting{ background:var(--soft-fill); color:var(--ink-muted); }
.cc-root .reaction[data-active="true"]{ background:var(--teal-soft); color:var(--ink); }
.cc-root .reaction:hover{ border-color:var(--teal); }
.cc-root .reaction[data-kind="same"] .dot{ background:var(--teal); }
.cc-root .reaction[data-kind="cheer"] .dot{ background:#4A90C2; }
.cc-root .reaction[data-kind="seen"] .dot{ background:var(--teal); }
.cc-root .comment-toggle:hover{ color:var(--ink); }
.cc-root .comment-bubble{ background:var(--soft-fill); }
.cc-root .comment-bubble .cname{ color:var(--ink); }
.cc-root .comment-input-row input{ background:var(--soft-fill); } .cc-root .comment-input-row input:focus{ border-color:var(--teal); background:#fff; }
.cc-root .side-card h3{ color:var(--ink); }
.cc-root .post-name{ color:var(--ink); }
.cc-root .post-name .badge{ color:var(--gold); }
.cc-root .prompt-card{ background:var(--gold-soft); border-color:rgba(245,166,35,0.30); }
.cc-root .prompt-card .eyebrow{ color:#B07A16; }
.cc-root .prompt-card .pq{ color:var(--ink); }
.cc-root .prompt-card .pb{ color:#8A6A2E; }
.cc-root .guideline-list li .dot{ background:var(--teal); }
.cc-root .room-row .rday{ color:var(--ink); }
.cc-root .spotlight-name{ color:var(--teal); }
.cc-root .side-links a{ background:var(--soft-fill); color:var(--ink); }
.cc-root .side-links a:hover{ background:var(--teal-soft); color:var(--teal); }
.cc-root .join-inner .eyebrow{ color:var(--teal); }
.cc-root .join-inner h2{ color:var(--ink); }
.cc-root .join-form input{ background:var(--soft-fill); } .cc-root .join-form input:focus{ outline-color:var(--teal); }
.cc-root .join-confirm{ color:var(--ink); }
.cc-root .cbadge{ background:var(--soft-fill); color:var(--ink-muted); }
.cc-root .cbadge[data-badge="Top Contributor"], .cc-root .cbadge[data-badge="Team"]{ background:var(--teal); color:#fff; }
.cc-root .cbadge[data-badge="Active Contributor"]{ background:var(--teal-soft); color:var(--teal); }
.cc-root .link-btn:hover{ color:var(--ink); }
.cc-root .img-btn:hover{ border-color:var(--teal); color:var(--ink); }
.cc-root .reply-input-row input{ background:var(--soft-fill); } .cc-root .reply-input-row input:focus{ border-color:var(--teal); }
.cc-root .modal-card textarea, .cc-root .modal-card input[type=text]{ background:var(--soft-fill); } .cc-root .modal-card textarea:focus, .cc-root .modal-card input:focus{ border-color:var(--teal); }
.cc-root .dm-unread{ background:var(--teal); color:#fff; }
.cc-root .dm-conv[data-active="true"]{ background:var(--teal-soft); }
.cc-root .dm-bubble.mine{ background:var(--ink); color:#fff; }
.cc-root .dm-bubble.theirs{ background:var(--soft-fill); color:var(--ink); }
.cc-root .dm-input input{ background:var(--soft-fill); } .cc-root .dm-input input:focus{ border-color:var(--teal); }
.cc-root .dm-tabs{ display:flex; border-bottom:1px solid var(--line); }
.cc-root .dm-tab{ flex:1; padding:10px; background:none; border:none; border-bottom:2px solid transparent; font-family:'Outfit',sans-serif; font-weight:700; font-size:13px; color:var(--faint); cursor:pointer; }
.cc-root .dm-tab[data-active="true"]{ color:var(--ink); border-bottom-color:var(--teal); }
.cc-root .group-ava{ width:38px; height:38px; border-radius:12px; background:var(--ink); color:#fff; display:flex; align-items:center; justify-content:center; font-family:'Outfit',sans-serif; font-weight:800; font-size:13px; flex-shrink:0; }
.cc-root .gm-sender{ font-family:'Outfit',sans-serif; font-weight:700; font-size:11px; color:var(--teal); margin-bottom:2px; }
.cc-root .member-results{ max-height:220px; overflow-y:auto; margin-top:10px; border:1px solid var(--line); border-radius:10px; }
.cc-root .member-row{ display:flex; align-items:center; gap:10px; padding:9px 12px; cursor:pointer; border-bottom:1px solid var(--line); font-size:13.5px; color:var(--ink); }
.cc-root .member-row:hover{ background:var(--soft-fill); }
.cc-root .pick-row{ display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
.cc-root .pick-chip{ background:var(--teal-soft); color:var(--teal); border-radius:999px; padding:4px 10px; font-size:12px; font-weight:600; cursor:pointer; }
.cc-root .featured-card{ border:1px solid var(--line-strong); border-left:3px solid var(--teal); }
.cc-root .featured-card .eyebrow{ color:var(--teal); display:block; margin-bottom:10px; }
.cc-root .featured-card .feat-head{ display:flex; align-items:center; gap:8px; margin-bottom:8px; }
.cc-root .featured-card .feat-name{ font-family:'Outfit',sans-serif; font-weight:700; font-size:13.5px; color:var(--ink); }
.cc-root .featured-card .feat-body{ font-size:13.5px; color:var(--ink-muted); line-height:1.5; margin:0; display:-webkit-box; -webkit-line-clamp:5; -webkit-box-orient:vertical; overflow:hidden; }
.cc-root .featured-card .feat-img{ margin-top:10px; border-radius:10px; border:1px solid var(--line); max-width:100%; display:block; }
.cc-root .msg-wrap{ display:flex; flex-direction:column; max-width:78%; }
.cc-root .msg-wrap.mine{ align-self:flex-end; align-items:flex-end; }
.cc-root .msg-wrap.theirs{ align-self:flex-start; align-items:flex-start; }
.cc-root .msg-wrap .dm-bubble{ max-width:100%; align-self:auto; }
.cc-root .like-btn{ display:inline-flex; align-items:center; gap:4px; background:none; border:none; cursor:pointer; color:var(--faint); font-size:11px; font-family:'Outfit',sans-serif; margin-top:3px; padding:1px 3px; }
.cc-root .like-btn:hover{ color:var(--teal); }
.cc-root .like-btn[data-liked="true"]{ color:var(--teal); }
.cc-root .like-btn svg{ width:13px; height:13px; }
.cc-root .react-bar{ display:flex; gap:5px; margin-top:4px; }
.cc-root .msg-wrap.mine .react-bar{ justify-content:flex-end; }
.cc-root .react-chip{ display:inline-flex; align-items:center; gap:3px; background:#fff; border:1.5px solid var(--line-strong); border-radius:999px; padding:2px 8px; font-size:11px; font-weight:700; font-family:'Outfit',sans-serif; color:var(--faint); line-height:1; transition:background .12s ease, border-color .12s ease, color .12s ease; }
.cc-root .react-chip:hover{ border-color:var(--teal); color:var(--teal); }
.cc-root .react-chip[data-active="true"]{ background:var(--teal-soft); border-color:transparent; color:var(--teal); }
.cc-root .react-chip svg{ width:13px; height:13px; }
.cc-root .typing-line{ padding:2px 16px 6px; font-size:11.5px; color:var(--teal); font-style:italic; font-family:'Manrope',sans-serif; }
.cc-root .dir-filters{ display:flex; gap:7px; flex-wrap:wrap; margin-top:14px; }
.cc-root .dir-filter{ background:#fff; border:1.5px solid var(--line-strong); border-radius:999px; padding:5px 12px; font-size:11.5px; font-weight:600; font-family:'Outfit',sans-serif; color:var(--ink-muted); transition:all .12s ease; }
.cc-root .dir-filter:hover{ border-color:var(--teal); color:var(--teal); }
.cc-root .dir-filter[data-active="true"]{ background:var(--teal); border-color:var(--teal); color:#fff; }
.cc-root .dir-card{ max-width:640px; }
.cc-root .dir-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:14px; margin-top:16px; max-height:60vh; overflow-y:auto; }
.cc-root .dir-member{ border:1px solid var(--line); border-radius:14px; padding:16px 12px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:6px; background:#fff; }
.cc-root .dir-member .avatar{ cursor:pointer; }
.cc-root .dir-name{ font-family:'Outfit',sans-serif; font-weight:700; font-size:13.5px; color:var(--ink); }
.cc-root .dir-bio{ font-size:11.5px; color:var(--ink-muted); line-height:1.4; margin:2px 0 0; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
@media(max-width:640px){ .cc-root .dir-grid{ grid-template-columns:repeat(2,1fr); } }

`;

const Avatar = ({ name, url, style, onClick, testid }) => {
  const av = initialsAvatar(name);
  const common = { className: "avatar", style, onClick, "data-testid": testid, title: name };
  if (url) return <img {...common} src={url.startsWith("http") ? url : `${BACKEND}${url}`} alt={name || "member"} />;
  return <div {...common} style={{ background: av.color, ...style }}>{av.initials}</div>;
};

const Badge = ({ label }) =>
  label ? <span className="cbadge" data-badge={label} data-testid="member-badge">{label}</span> : null;

const REACTIONS = [
  { kind: "heart", Icon: Heart },
  { kind: "celebrate", Icon: PartyPopper },
  { kind: "seen", Icon: Eye },
];

const ReactBar = ({ reactions, onReact, testidPrefix }) => {  const r = reactions || {};
  return (
    <div className="react-bar">
      {REACTIONS.map(({ kind, Icon }) => {
        const info = r[kind] || { count: 0, mine: false };
        return (
          <button key={kind} className="react-chip" data-active={!!info.mine}
            onClick={() => onReact(kind)} data-testid={`${testidPrefix}-${kind}`} aria-label={kind}>
            <Icon fill={info.mine ? "currentColor" : "none"} />
            {info.count > 0 ? info.count : ""}
          </button>
        );
      })}
    </div>
  );
};

const REPORT_REASONS = [
  ["spam", "Spam or scam"],
  ["harassment", "Harassment or bullying"],
  ["hate", "Hate or discrimination"],
  ["sexual", "Sexual or explicit content"],
  ["violence", "Violence or threats"],
  ["self_harm", "Self harm"],
  ["other", "Something else"],
];

export default function Community() {
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [resources, setResources] = useState(null);
  const [filter, setFilter] = useState("All");
  const [user, setUser] = useState(null);
  const [text, setText] = useState("");
  const [tag, setTag] = useState(null);
  const [openComments, setOpenComments] = useState(() => new Set());
  const [commentDraft, setCommentDraft] = useState({});
  const [joinEmail, setJoinEmail] = useState("");
  const [joinConfirm, setJoinConfirm] = useState(false);
  const [notice, setNotice] = useState("");
  const [prompt, setPrompt] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [replyOpen, setReplyOpen] = useState(() => new Set());
  const [replyDraft, setReplyDraft] = useState({});
  const [profileUid, setProfileUid] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ display_name: "", bio: "" });
  const [editAvatar, setEditAvatar] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmConvs, setDmConvs] = useState([]);
  const [dmActive, setDmActive] = useState(null);
  const [dmOther, setDmOther] = useState(null);
  const [dmMessages, setDmMessages] = useState([]);
  const [dmDraft, setDmDraft] = useState("");
  const [dmUnread, setDmUnread] = useState(0);
  const [dmSending, setDmSending] = useState(false);
  const dmLastTs = useRef("");
  const dmScrollRef = useRef(null);
  const [msgTab, setMsgTab] = useState("direct");
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupInfo, setGroupInfo] = useState(null);
  const [groupMsgs, setGroupMsgs] = useState([]);
  const [groupDraft, setGroupDraft] = useState("");
  const [groupSending, setGroupSending] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", topic: "" });
  const [groupPicks, setGroupPicks] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const groupLastTs = useRef("");
  const [dirOpen, setDirOpen] = useState(false);
  const [dirQuery, setDirQuery] = useState("");
  const [dirMembers, setDirMembers] = useState([]);
  const [dirBadge, setDirBadge] = useState("All");
  const [dmTyping, setDmTyping] = useState(null);
  const [groupTyping, setGroupTyping] = useState([]);
  const dmTypingSent = useRef(0);
  const groupTypingSent = useRef(0);
  const joinRef = useRef(null);
  const fileInputRef = useRef(null);

  const [blockedUids, setBlockedUids] = useState(() => new Set());
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState("spam");
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  const applyBlocked = (list) => {
    if (Array.isArray(list)) setBlockedUids(new Set(list.map((b) => b.uid).filter(Boolean)));
  };

  const openReport = (target) => {
    setReportTarget(target);
    setReportReason("spam");
    setReportNote("");
  };
  const submitReport = async () => {
    if (!reportTarget) return;
    setReportBusy(true);
    try {
      await api("/community/report", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...reportTarget.payload, reason: reportReason, note: reportNote }),
      });
      setReportTarget(null);
      setNotice("Thanks for the report. Our team will review it.");
    } catch (e) { setNotice("Could not send the report. Please try again."); }
    finally { setReportBusy(false); }
  };
  const blockUser = async (uid) => {
    if (!uid) return;
    try {
      await api("/community/block", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ uid }),
      });
      setBlockedUids((s) => new Set(s).add(uid));
      setProfileUid(null);
      setNotice("Blocked. You will not see their posts, and they cannot message or reply to you.");
      loadPosts();
    } catch (e) { setNotice("Could not block this member. Please try again."); }
  };
  const unblockUser = async (uid) => {
    if (!uid) return;
    try {
      await api("/community/unblock", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ uid }),
      });
      setBlockedUids((s) => { const n = new Set(s); n.delete(uid); return n; });
      setNotice("Unblocked.");
      loadPosts();
    } catch (e) { setNotice("Could not unblock. Please try again."); }
  };

  const api = async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, { credentials: "include", ...options });
    if (!res.ok) throw new Error(String(res.status));
    return res.json();
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/community/upload`, { method: "POST", credentials: "include", body: fd });
    if (!res.ok) throw new Error(String(res.status));
    return res.json();
  };

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setNotice("Please choose an image file."); return; }
    if (f.size > 5 * 1024 * 1024) { setNotice("Images need to be under 5 MB."); return; }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };
  const clearImage = () => {
    setImageFile(null);
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openProfile = async (uid) => {
    if (!uid) return;
    setProfileUid(uid);
    setProfileData(null);
    try { setProfileData(await api(`/community/members/${uid}`)); } catch (e) { setProfileData({ error: true }); }
  };

  const openEdit = () => {
    setEditForm({ display_name: user?.display_name || "", bio: user?.bio || "" });
    setEditAvatar(user?.avatar_url || "");
    setEditOpen(true);
  };
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      let avatar_url = editAvatar;
      if (editAvatar && editAvatar.startsWith("data:")) {
        // never happens: we upload immediately in onAvatarPick
      }
      const updated = await api("/community/auth/profile", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: editForm.display_name, bio: editForm.bio, avatar_url }),
      });
      setUser(updated);
      setEditOpen(false);
      loadPosts();
    } catch (e) { setNotice("Could not save your profile. Please try again."); }
    finally { setSavingProfile(false); }
  };
  const onAvatarPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith("image/")) return;
    if (f.size > 5 * 1024 * 1024) { setNotice("Avatar needs to be under 5 MB."); return; }
    try {
      const res = await uploadImage(f);
      setEditAvatar(res.url);
    } catch (err) { setNotice("Avatar upload failed. Please try again."); }
  };

  const toggleReply = (cid) => {
    setReplyOpen((s) => { const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });
  };
  const addReply = async (postId, commentId, value) => {
    const body = (value || "").trim();
    if (!body) return;
    if (!user) return promptLogin();
    try {
      const updated = await api(`/community/posts/${postId}/comments/${commentId}/replies`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      setPosts((list) => list.map((p) => (p.id === postId ? { ...updated } : p)));
      setReplyDraft((d) => ({ ...d, [commentId]: "" }));
    } catch (e) { if (e.message === "401") promptLogin(); }
  };

  // ---- Direct messages ----
  const loadUnread = async () => {
    try {
      const [d, g] = await Promise.all([api("/community/dm/unread_total"), api("/community/groups/unread_total")]);
      setDmUnread((d.count || 0) + (g.count || 0));
    } catch (e) {}
  };
  const loadConvs = async () => {
    try { const d = await api("/community/dm/conversations"); setDmConvs(d.conversations || []); } catch (e) {}
  };
  const loadMessages = async (reset) => {
    if (!dmActive) return;
    try {
      const after = reset ? "" : dmLastTs.current;
      const d = await api(`/community/dm/${encodeURIComponent(dmActive)}/messages${after ? `?after=${encodeURIComponent(after)}` : ""}`);
      setDmOther(d.other);
      setDmTyping(d.typing || null);
      if (reset) {
        setDmMessages(d.messages);
      } else if (d.messages.length) {
        setDmMessages((m) => [...m, ...d.messages]);
      }
      const all = reset ? d.messages : d.messages;
      if (all.length) dmLastTs.current = all[all.length - 1].created_at;
      if (d.messages.length) { setDmUnread(0); loadConvs(); }
    } catch (e) {}
  };
  const openMessages = async () => { setDmOpen(true); await loadConvs(); };
  const openThread = async (conversation_id, other) => {
    setDmActive(conversation_id);
    setDmOther(other || null);
    setDmMessages([]);
    setDmTyping(null);
    dmLastTs.current = "";
    setTimeout(() => loadMessages(true), 0);
  };
  const startDMWith = async (uid) => {
    if (!user) return promptLogin();
    try {
      const d = await api("/community/dm/start", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid }),
      });
      setProfileUid(null);
      setDmOpen(true);
      await loadConvs();
      openThread(d.conversation_id, d.other);
    } catch (e) { setNotice("Could not open that conversation."); }
  };
  const sendDM = async () => {
    const body = dmDraft.trim();
    if (!body || !dmActive) return;
    setDmSending(true);
    try {
      const msg = await api(`/community/dm/${encodeURIComponent(dmActive)}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      setDmMessages((m) => [...m, msg]);
      dmLastTs.current = msg.created_at;
      setDmDraft("");
      loadConvs();
    } catch (e) { setNotice("Message failed to send."); }
    finally { setDmSending(false); }
  };

  useEffect(() => {
    if (!user) { setDmUnread(0); return; }
    loadUnread();
    const t = setInterval(loadUnread, 10000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!dmActive) return;
    const t = setInterval(() => loadMessages(false), 4000);
    return () => clearInterval(t);
  }, [dmActive]);

  useEffect(() => {
    if (!dmOpen) return;
    const t = setInterval(loadConvs, 8000);
    return () => clearInterval(t);
  }, [dmOpen]);

  useEffect(() => {
    if (dmScrollRef.current) dmScrollRef.current.scrollTop = dmScrollRef.current.scrollHeight;
  }, [dmMessages, groupMsgs]);

  // ---- Group chats ----
  const loadGroups = async () => {
    try { const d = await api("/community/groups"); setGroups(d.groups || []); } catch (e) {}
  };
  const loadGroupMsgs = async (reset) => {
    if (!activeGroup) return;
    try {
      const after = reset ? "" : groupLastTs.current;
      const d = await api(`/community/groups/${activeGroup}/messages${after ? `?after=${encodeURIComponent(after)}` : ""}`);
      setGroupInfo(d.group);
      setGroupTyping(d.typing || []);
      if (reset) setGroupMsgs(d.messages);
      else if (d.messages.length) setGroupMsgs((m) => [...m, ...d.messages]);
      if (d.messages.length) { groupLastTs.current = d.messages[d.messages.length - 1].created_at; loadGroups(); loadUnread(); }
    } catch (e) {}
  };
  const openGroup = (gid) => {
    setActiveGroup(gid); setGroupInfo(null); setGroupMsgs([]); setGroupTyping([]); groupLastTs.current = "";
    setTimeout(() => loadGroupMsgs(true), 0);
  };
  const sendGroup = async () => {
    const body = groupDraft.trim();
    if (!body || !activeGroup) return;
    setGroupSending(true);
    try {
      const msg = await api(`/community/groups/${activeGroup}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body }),
      });
      setGroupMsgs((m) => [...m, msg]);
      groupLastTs.current = msg.created_at;
      setGroupDraft(""); loadGroups();
    } catch (e) { setNotice("Message failed to send."); }
    finally { setGroupSending(false); }
  };
  const searchMembers = async (q) => {
    setMemberQuery(q);
    try { const d = await api(`/community/members${q ? `?q=${encodeURIComponent(q)}` : ""}`); setMemberResults(d.members || []); } catch (e) {}
  };
  const togglePick = (m) => {
    setGroupPicks((p) => p.some((x) => x.uid === m.uid) ? p.filter((x) => x.uid !== m.uid) : [...p, m]);
  };
  const createGroup = async () => {
    const name = newGroup.name.trim();
    if (!name) { setNotice("Give your group a name."); return; }
    try {
      const g = await api("/community/groups", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, topic: newGroup.topic, member_uids: groupPicks.map((p) => p.uid) }),
      });
      setNewGroupOpen(false); setNewGroup({ name: "", topic: "" }); setGroupPicks([]); setMemberQuery(""); setMemberResults([]);
      await loadGroups();
      setMsgTab("groups");
      openGroup(g.id);
    } catch (e) { setNotice("Could not create the group."); }
  };
  const addMemberToGroup = async (uid) => {
    try {
      const d = await api(`/community/groups/${activeGroup}/members`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ uid }),
      });
      setGroupInfo((g) => g ? { ...g, member_count: d.member_count, members: d.members } : g);
      setAddMemberOpen(false); setMemberQuery(""); setMemberResults([]);
    } catch (e) { setNotice("Could not add that member."); }
  };
  const leaveGroup = async () => {
    if (!activeGroup) return;
    try {
      await api(`/community/groups/${activeGroup}/leave`, { method: "POST" });
      setActiveGroup(null); setGroupInfo(null); setGroupMsgs([]);
      loadGroups();
    } catch (e) {}
  };
  const openMessagesGroups = async () => { setDmOpen(true); setMsgTab("groups"); await loadGroups(); };

  const reactMessage = async (which, mid, kind) => {
    const path = which === "dm"
      ? `/community/dm/${dmActive}/messages/${mid}/like`
      : `/community/groups/${activeGroup}/messages/${mid}/like`;
    try {
      const r = await api(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind }) });
      const setter = which === "dm" ? setDmMessages : setGroupMsgs;
      setter((list) => list.map((m) => (m.id === mid ? { ...m, reactions: r.reactions } : m)));
    } catch (e) {}
  };

  const notifyTyping = (which) => {
    const now = Date.now();
    if (which === "dm") {
      if (!dmActive || now - dmTypingSent.current < 2500) return;
      dmTypingSent.current = now;
      api(`/community/dm/${dmActive}/typing`, { method: "POST" }).catch(() => {});
    } else {
      if (!activeGroup || now - groupTypingSent.current < 2500) return;
      groupTypingSent.current = now;
      api(`/community/groups/${activeGroup}/typing`, { method: "POST" }).catch(() => {});
    }
  };

  const loadDirectory = async (q = dirQuery, badge = dirBadge) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (badge && badge !== "All") params.set("badge", badge);
    const qs = params.toString();
    try { const d = await api(`/community/members${qs ? `?${qs}` : ""}`); setDirMembers(d.members || []); } catch (e) {}
  };
  const openDirectory = async () => {
    if (!user) return promptLogin();
    setDirOpen(true); setDirQuery(""); setDirBadge("All"); await loadDirectory("", "All");
  };
  const searchDirectory = (q) => { setDirQuery(q); loadDirectory(q, dirBadge); };
  const filterDirectory = (badge) => { setDirBadge(badge); loadDirectory(dirQuery, badge); };

  useEffect(() => {
    if (!activeGroup) return;
    const t = setInterval(() => loadGroupMsgs(false), 4000);
    return () => clearInterval(t);
  }, [activeGroup]);

  useEffect(() => {
    if (!dmOpen || msgTab !== "groups") return;
    loadGroups();
    const t = setInterval(loadGroups, 8000);
    return () => clearInterval(t);
  }, [dmOpen, msgTab]);


  const loadPosts = async () => {
    try {
      const data = await api("/community/posts");
      setPosts(data.posts || []);
      setUser(data.user || null);
      setFeatured(data.featured || null);
      applyBlocked(data.blocked);
    } catch (e) { /* not signed in: feed stays gated */ }
  };

  const loadResources = async () => {
    try { setResources(await api("/community/resources")); } catch (e) {}
  };

  const loadPrompt = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      const data = await api(`/community/prompt${tz ? `?tz=${encodeURIComponent(tz)}` : ""}`);
      setPrompt(data);
    } catch (e) {
      setPrompt({ question: "What is the one thing worth finishing?", body: "Select one meaningful priority and protect time for it." });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("login") === "ok") setNotice("You are signed in. Post whenever you are ready.");
    if (params.get("login") === "failed") setNotice("That login link expired or was already used. Request a fresh one below.");
    if (params.get("login")) window.history.replaceState(null, "", "/community");
    (async () => {
      let u = null;
      try { u = await api("/community/auth/me"); } catch (e) { u = null; }
      setUser(u);
      if (u) { applyBlocked(u.blocked); loadPosts(); loadResources(); }
    })();
    loadPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const promptLogin = () => {
    setNotice("Reading is open to everyone. Sign in with your guide email to join in.");
    joinRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => document.getElementById("cc-join-email")?.focus(), 400);
  };

  const submitPost = async () => {
    const body = text.trim();
    if (!body && !imageFile) return;
    if (!user) return promptLogin();
    setUploading(true);
    try {
      let image_url = null;
      if (imageFile) {
        const up = await uploadImage(imageFile);
        image_url = up.url;
      }
      const post = await api("/community/posts", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body, tag: tag || "Check in", image_url }),
      });
      setPosts((p) => [post, ...p]);
      setText(""); setTag(null); setFilter("All"); clearImage();
    } catch (e) {
      if (e.message === "401") promptLogin();
      else setNotice("Could not post right now. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const react = async (postId, kind) => {
    if (!user) return promptLogin();
    try {
      const updated = await api(`/community/posts/${postId}/react`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind }),
      });
      setPosts((list) => list.map((p) => (p.id === postId ? { ...updated } : p)));
    } catch (e) {
      if (e.message === "401") promptLogin();
    }
  };

  const toggleComments = (postId) => {
    setOpenComments((s) => {
      const n = new Set(s);
      n.has(postId) ? n.delete(postId) : n.add(postId);
      return n;
    });
  };

  const addComment = async (postId, value) => {
    const body = (value || "").trim();
    if (!body) return;
    if (!user) return promptLogin();
    try {
      const updated = await api(`/community/posts/${postId}/comments`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      setPosts((list) => list.map((p) => (p.id === postId ? { ...updated } : p)));
      setCommentDraft((d) => ({ ...d, [postId]: "" }));
      setOpenComments((s) => new Set(s).add(postId));
    } catch (e) {
      if (e.message === "401") promptLogin();
    }
  };

  const submitJoin = async (e) => {
    e.preventDefault();
    const email = joinEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      await api("/community/auth/request", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, origin_url: window.location.origin }),
      });
    } catch (e2) { /* generic response */ }
    setJoinConfirm(true);
  };

  const logout = async () => {
    try { await api("/community/auth/logout", { method: "POST" }); } catch (e) { /* no-op */ }
    setUser(null);
    loadPosts();
  };

  const scrollJoin = () => {
    joinRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => document.getElementById("cc-join-email")?.focus(), 400);
  };

  const visible = useMemo(
    () => posts.filter((p) => filter === "All" || p.tag === filter),
    [posts, filter]
  );
  const meName = user ? user.display_name : "You";

  return (
    <>
    <div className="cc-root" data-testid="community-page">
      <style>{CSS}</style>

      <div className="nav">
        <div className="nav-inner">
          <a href="/" className="inline-flex items-center" data-testid="brand-logo"><img src={LOGO_DARK_URL} alt="CommandCenterADHD" width="1085" height="537" className="h-9 sm:h-10 w-auto object-contain" /></a>
          <div className="nav-links">
            <a href="#feed">Feed</a>
            <a href="#ama">AMA</a>
            <a href="#guidelines">Guidelines</a>
            <a href="/">Get the system</a>
          </div>
          {user ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button className="btn btn-ghost btn-sm" onClick={openDirectory} data-testid="open-members-button">Members</button>
              <button className="btn btn-ghost btn-sm nav-msg-btn" onClick={openMessages} data-testid="open-messages-button">
                Messages
                {dmUnread > 0 && <span className="dm-unread" data-testid="nav-dm-unread">{dmUnread}</span>}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={openEdit} data-testid="edit-profile-button">Edit profile</button>
              <button className="btn btn-ghost btn-sm" onClick={logout} data-testid="community-logout">Sign out</button>
            </div>
          ) : (
            <button className="btn btn-primary" onClick={scrollJoin} data-testid="nav-join-button">Join the community</button>
          )}
        </div>
      </div>

      <section className="hero">
        <div className="wrap hero-grid">
          <div>
            <span className="eyebrow">For Command Center owners</span>
            <h1>A quiet place to share the <em>wins</em> and the hard parts.</h1>
            <p>The CommandCenterADHD community is a small private space for Command Center owners to share wins and hard parts. Post a win, ask a question, or just check in for the day. There is no pressure to perform here, and nothing to keep up with.</p>
            <div className="hero-ctas">
              {user ? (
                <button className="btn btn-primary" onClick={() => document.getElementById("feed").scrollIntoView()} data-testid="hero-open-feed">Open the feed</button>
              ) : (
                <button className="btn btn-primary" onClick={scrollJoin} data-testid="hero-join-button">Join the community</button>
              )}
              <button className="btn btn-ghost" onClick={() => document.getElementById("feed").scrollIntoView()}>See the feed</button>
            </div>
            <div className="hero-meta">
              <span className="avatar-stack">
                {["Jamie M.", "Alex T.", "Robin C.", "Priya R.", "Sam O."].map((n) => {
                  const av = initialsAvatar(n);
                  return <span key={n} className="mini" style={{ background: av.color }}>{av.initials[0]}</span>;
                })}
              </span>
              <span>Members check in at their own pace. No streaks, no scoreboard.</span>
            </div>
          </div>
          <div className="peek">
            <div className="post" style={{ margin: 0, padding: "16px 4px 4px", border: "none", boxShadow: "none" }}>
              <div className="post-head">
                <div className="avatar" style={{ background: "#3A7CA5" }}>JM</div>
                <div className="post-meta">
                  <div className="post-name">Jamie M.</div>
                  <div className="post-sub">2 hours ago</div>
                </div>
                <span className="post-tag Win">Win</span>
              </div>
              <div className="post-body">Finally opened the email I had been avoiding for nine days. It took four minutes. The dread was doing more work than the task ever needed.</div>
              <div className="post-actions" style={{ borderTop: "none", paddingTop: 0 }}>
                <span className="reaction" data-kind="same" data-active="true"><span className="dot" />Same &middot; 23</span>
                <span className="reaction" data-kind="cheer"><span className="dot" />Cheering you on &middot; 8</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="wrap content" id="feed">
        <main>
          {notice && (
            <div className="card side-card" style={{ marginBottom: 18, borderColor: "var(--gold)" }} data-testid="community-notice">
              <p style={{ margin: 0, color: "var(--ink-muted)", fontSize: 13.5 }}>{notice}</p>
            </div>
          )}

          <div className="card composer">
            {user ? (
              <>
                <div className="composer-top">
                  <Avatar name={meName} url={user?.avatar_url} style={{ background: "var(--ink)" }} />
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Share a win, ask a question, or just check in..."
                    rows={1}
                    data-testid="composer-text"
                  />
                </div>
                {imagePreview && (
                  <div className="img-preview" data-testid="composer-image-preview">
                    <img src={imagePreview} alt="Selected upload preview" />
                    <button type="button" className="rm" onClick={clearImage} aria-label="Remove image" data-testid="composer-image-remove">×</button>
                  </div>
                )}
                <div className="composer-tags">
                  {["Win", "Question", "Check in", "Just venting"].map((t) => (
                    <button key={t} className="chip" data-tag={t} data-active={tag === t}
                      onClick={() => setTag(tag === t ? null : t)} data-testid={`composer-chip-${tagClass(t)}`}>{t}</button>
                  ))}
                </div>
                <div className="composer-actions">
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={pickImage} style={{ display: "none" }} data-testid="composer-image-input" />
                    <button type="button" className="img-btn" onClick={() => fileInputRef.current?.click()} data-testid="composer-image-button">
                      <span aria-hidden>+</span> Add image
                    </button>
                    <span style={{ fontSize: 12, color: "var(--faint)", fontFamily: "'Outfit',sans-serif" }}>Posting as {meName}</span>
                  </div>
                  <button className="btn btn-teal btn-sm" onClick={submitPost} disabled={uploading} data-testid="composer-post">
                    {uploading ? "Posting..." : "Post"}
                  </button>
                </div>
              </>
            ) : (
              <div className="composer-signin" data-testid="community-gate">
                <p>This is a private space for Command Center owners. Sign in with the email you used at purchase to read the feed and join the conversation.</p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button className="btn btn-teal btn-sm" onClick={scrollJoin} data-testid="composer-signin-button">Email me a login link</button>
                  <a className="btn btn-ghost btn-sm" href="/#pricing" data-testid="community-buy-cta">Get the Command Center</a>
                </div>
              </div>
            )}
          </div>

          {user && (
          <>
          <div className="filterbar" id="filterBar">
            {[["All", "All posts"], ["Win", "Wins"], ["Question", "Questions"], ["Check in", "Check ins"]].map(([f, label]) => (
              <button key={f} data-filter={f} data-active={filter === f} onClick={() => setFilter(f)} data-testid={`filter-${tagClass(f)}`}>{label}</button>
            ))}
          </div>

          <div id="postList">
            {visible.map((p) => {
              const isOpen = openComments.has(p.id);
              return (
                <div className={`card post ${p.pinned ? "pinned" : ""}`} key={p.id} data-testid={`post-${p.id}`}>
                  <div className="post-head">
                    <Avatar name={p.name} url={p.avatar_url} onClick={() => openProfile(p.uid)} style={{ cursor: p.uid ? "pointer" : "default" }} testid={`post-avatar-${p.id}`} />
                    <div className="post-meta">
                      <div className="post-name">
                        <button className="name-btn" onClick={() => openProfile(p.uid)} data-testid={`post-author-${p.id}`}>{p.name}</button>
                        <Badge label={p.badge} />
                      </div>
                      <div className="post-sub">{p.time}</div>
                    </div>
                    <span className={`post-tag ${tagClass(p.tag)}`}>{p.tag}</span>
                  </div>
                  {p.body && <div className="post-body">{p.body}</div>}
                  {p.image_url && (
                    <img className="post-img" src={`${BACKEND}${p.image_url}`} alt="Shared by a member" loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      data-testid={`post-image-${p.id}`} />
                  )}
                  <div className="post-actions">
                    {[["same", "Same"], ["cheer", "Cheering you on"], ["seen", "Seen"]].map(([k, label]) => (
                      <button key={k} className="reaction" data-kind={k} data-active={p.myReaction === k}
                        onClick={() => react(p.id, k)} data-testid={`react-${k}-${p.id}`}>
                        <span className="dot" />{label} &middot; {p.reactions[k]}
                      </button>
                    ))}
                    <button className="comment-toggle" onClick={() => toggleComments(p.id)} data-testid={`comment-toggle-${p.id}`}>
                      {p.comments.length ? `${p.comments.length} comment${p.comments.length > 1 ? "s" : ""}` : "Comment"}
                    </button>
                    {user && p.uid && p.uid !== user.uid && (
                      <button className="link-btn" style={{ marginLeft: 12 }}
                        onClick={() => openReport({ label: "post", payload: { target_type: "post", post_id: p.id } })}
                        data-testid={`report-post-${p.id}`}>Report</button>
                    )}
                  </div>
                  {isOpen && (
                    <div className="comments" data-open="true">
                      {p.comments.map((c, i) => (
                        <div key={c.id || i}>
                          <div className="comment">
                            <Avatar name={c.name} url={c.avatar_url} onClick={() => openProfile(c.uid)} style={{ cursor: c.uid ? "pointer" : "default" }} />
                            <div style={{ flex: 1 }}>
                              <div className="comment-bubble">
                                <span className="cname">
                                  <button className="name-btn" onClick={() => openProfile(c.uid)}>{c.name}</button>{" "}
                                  <Badge label={c.badge} />
                                </span>
                                {c.body}
                              </div>
                              {c.id && (
                                <button className="link-btn" onClick={() => toggleReply(c.id)} data-testid={`reply-toggle-${c.id}`}>
                                  {c.replies?.length ? `${c.replies.length} repl${c.replies.length > 1 ? "ies" : "y"}` : "Reply"}
                                </button>
                              )}
                              {c.id && user && c.uid && c.uid !== user.uid && (
                                <button className="link-btn" style={{ marginLeft: 12 }}
                                  onClick={() => openReport({ label: "comment", payload: { target_type: "comment", post_id: p.id, comment_id: c.id } })}
                                  data-testid={`report-comment-${c.id}`}>Report</button>
                              )}
                            </div>
                          </div>
                          {(c.replies || []).map((r, ri) => (
                            <div className="reply" key={r.id || ri}>
                              <Avatar name={r.name} url={r.avatar_url} onClick={() => openProfile(r.uid)} style={{ cursor: r.uid ? "pointer" : "default" }} />
                              <div className="comment-bubble">
                                <span className="cname">
                                  <button className="name-btn" onClick={() => openProfile(r.uid)}>{r.name}</button>{" "}
                                  <Badge label={r.badge} />
                                </span>
                                {r.body}
                              </div>
                            </div>
                          ))}
                          {c.id && replyOpen.has(c.id) && (
                            <div className="reply-input-row">
                              <input
                                type="text"
                                placeholder={`Reply to ${c.name}...`}
                                value={replyDraft[c.id] || ""}
                                onChange={(e) => setReplyDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) addReply(p.id, c.id, e.target.value); }}
                                data-testid={`reply-input-${c.id}`}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="comment-input-row">
                        <Avatar name={meName} url={user?.avatar_url} style={{ background: "var(--ink)", width: 28, height: 28, fontSize: 10.5 }} />
                        <input
                          type="text"
                          placeholder="Write a comment..."
                          value={commentDraft[p.id] || ""}
                          onChange={(e) => setCommentDraft((d) => ({ ...d, [p.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) addComment(p.id, e.target.value); }}
                          data-testid={`comment-input-${p.id}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
          )}
        </main>

        <aside>
          {featured && (
            <div className="card side-card featured-card" data-testid="featured-post">
              <span className="eyebrow">Post of the week</span>
              <div className="feat-head">
                <Avatar name={featured.name} url={featured.avatar_url} style={{ width: 30, height: 30, fontSize: 11 }} onClick={() => openProfile(featured.uid)} />
                <button className="name-btn feat-name" onClick={() => openProfile(featured.uid)}>{featured.name}</button>
              </div>
              <p className="feat-body">{featured.body}</p>
              {featured.image_url && <img className="feat-img" src={`${BACKEND}${featured.image_url}`} alt="Featured post" loading="lazy" />}
            </div>
          )}
          <div className="card side-card prompt-card" data-testid="weekly-prompt">
            <span className="eyebrow">This week's gentle prompt</span>
            <p className="pq">{prompt ? prompt.question : "Loading..."}</p>
            {prompt?.body && <p className="pb">{prompt.body}</p>}
          </div>
          <div className="card side-card" id="guidelines">
            <h3>How this space works</h3>
            <ul className="guideline-list">
              <li><span className="dot" />Advice only when someone asks for it.</li>
              <li><span className="dot" />No judgment here about any kind of day.</li>
              <li><span className="dot" />This is not a competition. There is no scoreboard.</li>
              <li><span className="dot" />Low energy days are welcome exactly as they are.</li>
            </ul>
          </div>
          <div className="card side-card" id="ama">
            <h3>AMA: Ask Me Anything</h3>
            <p style={{ margin: "0 0 12px", color: "var(--ink-muted)", fontSize: 12.8 }}>Bring a question and we work through it together. Drop in when it suits you.</p>
            <div className="room-row"><span className="rday">Tuesday</span><span className="rtime">10:00 AM PT, drop in</span></div>
            <div className="room-row"><span className="rday">Thursday</span><span className="rtime">4:00 PM PT, drop in</span></div>
            <div className="room-row"><span className="rday">Sunday</span><span className="rtime">Weekly reset, 5:00 PM PT</span></div>
          </div>
          <div className="card side-card">
            <h3>Member spotlight</h3>
            <p className="spotlight-quote">"I stopped waiting to feel ready. I open Today and read the next step. That is the whole trick."</p>
            <span className="spotlight-name">Priya R., member since March</span>
          </div>
          {user && (
          <div className="card side-card">
            <h3>Quick links</h3>
            <div className="side-links">
              <a href="/guides" data-testid="quicklink-setup-videos">Watch the setup videos <span>&rsaquo;</span></a>
              {resources?.notion_url && (
                <a href={resources.notion_url} target="_blank" rel="noreferrer" data-testid="quicklink-workspace">Open your workspace <span>&rsaquo;</span></a>
              )}
              <a href="#guidelines" data-testid="quicklink-guidelines">Community guidelines <span>&rsaquo;</span></a>
            </div>
          </div>
          )}
        </aside>
      </div>

      <section className="join-section" ref={joinRef}>
        <div className="wrap join-inner">
          <span className="eyebrow">Free for Command Center owners</span>
          <h2>Come as you are today.</h2>
          <p>No streaks to keep and no leaderboard to lose. Just people who understand why a small win counts.</p>
          {joinConfirm ? (
            <p className="join-confirm" data-testid="join-confirm">If you own the Command Center, a one time login link is on its way to your inbox.</p>
          ) : (
            <form className="join-form" onSubmit={submitJoin}>
              <input id="cc-join-email" type="email" placeholder="name@email.com" required value={joinEmail}
                onChange={(e) => setJoinEmail(e.target.value)} data-testid="join-email" />
              <button className="btn btn-teal" type="submit" data-testid="join-submit">Join the community</button>
            </form>
          )}
          <p className="join-note">Open to everyone who owns the Command Center. One click, no password to remember.</p>
        </div>
      </section>

      <div className="foot-brand">
        <div className="foot-logo"><img src={LOGO_DARK_URL} alt="CommandCenterADHD" width="1085" height="537" className="h-9 w-auto object-contain" /></div>
        <p>Command Center Community &middot; A calm corner for Command Center owners.</p>
      </div>

      {dmOpen && (
        <div className="dm-overlay" onClick={() => { setDmOpen(false); setDmActive(null); setActiveGroup(null); }} data-testid="dm-overlay">
          <div className="dm-card" data-mobile-view={(msgTab === "direct" ? dmActive : activeGroup) ? "thread" : "list"} onClick={(e) => e.stopPropagation()}>
            <div className="dm-list">
              <div className="dm-list-head">
                <strong style={{ fontSize: 15 }}>Messages</strong>
                <button className="link-btn" style={{ fontSize: 18 }} onClick={() => { setDmOpen(false); setDmActive(null); setActiveGroup(null); }} aria-label="Close messages" data-testid="dm-close">×</button>
              </div>
              <div className="dm-tabs">
                <button className="dm-tab" data-active={msgTab === "direct"} onClick={() => setMsgTab("direct")} data-testid="dm-tab-direct">Direct</button>
                <button className="dm-tab" data-active={msgTab === "groups"} onClick={() => { setMsgTab("groups"); loadGroups(); }} data-testid="dm-tab-groups">Groups</button>
              </div>
              <div style={{ overflowY: "auto", flex: 1 }}>
                {msgTab === "direct" ? (
                  dmConvs.length === 0 ? (
                    <div className="dm-empty" data-testid="dm-empty-list">No conversations yet. Open a member profile and choose Send a message to start one.</div>
                  ) : (
                    dmConvs.map((c) => (
                      <div key={c.conversation_id} className="dm-conv" data-active={dmActive === c.conversation_id}
                        onClick={() => { setActiveGroup(null); openThread(c.conversation_id, c.other); }} data-testid={`dm-conv-${c.other.uid}`}>
                        <Avatar name={c.other.display_name} url={c.other.avatar_url} style={{ width: 38, height: 38, fontSize: 13 }} />
                        <div className="meta">
                          <div className="nm">{c.other.display_name}</div>
                          <div className="lm">{c.last_sender_is_me ? "You: " : ""}{c.last_message}</div>
                        </div>
                        {c.unread > 0 && <span className="dm-unread">{c.unread}</span>}
                      </div>
                    ))
                  )
                ) : (
                  <>
                    <button className="btn btn-teal btn-sm" style={{ margin: "12px 16px", width: "calc(100% - 32px)" }}
                      onClick={() => { setNewGroupOpen(true); searchMembers(""); }} data-testid="new-group-button">+ New group</button>
                    {groups.length === 0 ? (
                      <div className="dm-empty" data-testid="group-empty-list">No groups yet. Create one and add members by name.</div>
                    ) : (
                      groups.map((g) => (
                        <div key={g.id} className="dm-conv" data-active={activeGroup === g.id}
                          onClick={() => { setDmActive(null); openGroup(g.id); }} data-testid={`group-conv-${g.id}`}>
                          <div className="group-ava">{(g.name || "G").slice(0, 2).toUpperCase()}</div>
                          <div className="meta">
                            <div className="nm">{g.name}</div>
                            <div className="lm">{g.last_message || `${g.member_count} members`}</div>
                          </div>
                          {g.unread > 0 && <span className="dm-unread">{g.unread}</span>}
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="dm-thread">
              {msgTab === "direct" ? (
                !dmActive ? (
                  <div className="dm-empty">Choose a conversation to read and reply.</div>
                ) : (
                  <>
                    <div className="dm-thread-head">
                      <button className="link-btn dm-back" onClick={() => setDmActive(null)} aria-label="Back" style={{ fontSize: 18 }}>‹</button>
                      <Avatar name={dmOther?.display_name} url={dmOther?.avatar_url} style={{ width: 34, height: 34, fontSize: 12 }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }} data-testid="dm-thread-name">{dmOther?.display_name || "Member"}</div>
                        {dmOther?.badge && <div style={{ marginTop: 2 }}><Badge label={dmOther.badge} /></div>}
                      </div>
                      {dmOther?.uid && (
                        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                          <button className="link-btn" onClick={() => openReport({ label: "conversation", payload: { target_type: "dm", conversation_id: dmActive } })} data-testid="dm-report">Report</button>
                          {blockedUids.has(dmOther.uid)
                            ? <button className="link-btn" onClick={() => unblockUser(dmOther.uid)} data-testid="dm-unblock">Unblock</button>
                            : <button className="link-btn" onClick={() => blockUser(dmOther.uid)} data-testid="dm-block">Block</button>}
                        </div>
                      )}
                    </div>
                    <div className="dm-msgs" ref={dmScrollRef} data-testid="dm-messages">
                      {dmMessages.length === 0 ? (
                        <div className="dm-empty">No messages yet. Say hello.</div>
                      ) : (
                        dmMessages.map((m) => (
                          <div key={m.id} className={`msg-wrap ${m.mine ? "mine" : "theirs"}`}>
                            <div className={`dm-bubble ${m.mine ? "mine" : "theirs"}`} data-testid={`dm-msg-${m.mine ? "mine" : "theirs"}`}>{m.body}</div>
                            <ReactBar reactions={m.reactions} onReact={(kind) => reactMessage("dm", m.id, kind)} testidPrefix={`dm-react-${m.id}`} />
                          </div>
                        ))
                      )}
                    </div>
                    {dmTyping && <div className="typing-line" data-testid="dm-typing">{dmTyping} is typing…</div>}
                    <div className="dm-input">
                      <input type="text" placeholder="Write a message..." value={dmDraft} maxLength={2000}
                        onChange={(e) => { setDmDraft(e.target.value); notifyTyping("dm"); }}
                        onKeyDown={(e) => { if (e.key === "Enter" && dmDraft.trim()) sendDM(); }}
                        data-testid="dm-input" />
                      <button className="btn btn-teal btn-sm" onClick={sendDM} disabled={dmSending || !dmDraft.trim()} data-testid="dm-send">Send</button>
                    </div>
                  </>
                )
              ) : (
                !activeGroup ? (
                  <div className="dm-empty">Choose a group, or create one to chat with a few members.</div>
                ) : (
                  <>
                    <div className="dm-thread-head">
                      <button className="link-btn dm-back" onClick={() => setActiveGroup(null)} aria-label="Back" style={{ fontSize: 18 }}>‹</button>
                      <div className="group-ava" style={{ width: 34, height: 34, fontSize: 12 }}>{(groupInfo?.name || "G").slice(0, 2).toUpperCase()}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }} data-testid="group-thread-name">{groupInfo?.name || "Group"}</div>
                        <div style={{ fontSize: 11.5, color: "var(--faint)" }}>{groupInfo?.member_count || 0} members{groupInfo?.topic ? ` · ${groupInfo.topic}` : ""}</div>
                      </div>
                      <button className="link-btn" onClick={() => { setAddMemberOpen(true); searchMembers(""); }} data-testid="group-add-member-button">Add</button>
                      <button className="link-btn" onClick={leaveGroup} data-testid="group-leave-button">Leave</button>
                    </div>
                    <div className="dm-msgs" ref={dmScrollRef} data-testid="group-messages">
                      {groupMsgs.length === 0 ? (
                        <div className="dm-empty">No messages yet. Start the conversation.</div>
                      ) : (
                        groupMsgs.map((m) => (
                          <div key={m.id} className={`msg-wrap ${m.mine ? "mine" : "theirs"}`}>
                            <div className={`dm-bubble ${m.mine ? "mine" : "theirs"}`} data-testid={`group-msg-${m.mine ? "mine" : "theirs"}`}>
                              {!m.mine && <div className="gm-sender">{m.sender_name}</div>}
                              {m.body}
                            </div>
                            <ReactBar reactions={m.reactions} onReact={(kind) => reactMessage("group", m.id, kind)} testidPrefix={`group-react-${m.id}`} />
                          </div>
                        ))
                      )}
                    </div>
                    {groupTyping.length > 0 && <div className="typing-line" data-testid="group-typing">{groupTyping.join(", ")} {groupTyping.length === 1 ? "is" : "are"} typing…</div>}
                    <div className="dm-input">
                      <input type="text" placeholder="Message the group..." value={groupDraft} maxLength={2000}
                        onChange={(e) => { setGroupDraft(e.target.value); notifyTyping("group"); }}
                        onKeyDown={(e) => { if (e.key === "Enter" && groupDraft.trim()) sendGroup(); }}
                        data-testid="group-input" />
                      <button className="btn btn-teal btn-sm" onClick={sendGroup} disabled={groupSending || !groupDraft.trim()} data-testid="group-send">Send</button>
                    </div>
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {newGroupOpen && (
        <div className="modal-overlay" onClick={() => setNewGroupOpen(false)} data-testid="new-group-modal">
          <div className="modal-card" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="link-btn" style={{ position: "absolute", top: 14, right: 18, fontSize: 20 }} onClick={() => setNewGroupOpen(false)} aria-label="Close">×</button>
            <h3>Create a group</h3>
            <label className="field-label" htmlFor="grp-name">Group name</label>
            <input id="grp-name" type="text" maxLength={60} value={newGroup.name} onChange={(e) => setNewGroup((f) => ({ ...f, name: e.target.value }))} data-testid="group-name-input" />
            <label className="field-label" htmlFor="grp-topic">Topic (optional)</label>
            <input id="grp-topic" type="text" maxLength={120} value={newGroup.topic} onChange={(e) => setNewGroup((f) => ({ ...f, topic: e.target.value }))} data-testid="group-topic-input" />
            <label className="field-label">Add members by name</label>
            <input type="text" placeholder="Search members..." value={memberQuery} onChange={(e) => searchMembers(e.target.value)} data-testid="group-member-search" />
            {groupPicks.length > 0 && (
              <div className="pick-row">
                {groupPicks.map((p) => (
                  <span key={p.uid} className="pick-chip" onClick={() => togglePick(p)} data-testid={`group-pick-${p.uid}`}>{p.display_name} ×</span>
                ))}
              </div>
            )}
            <div className="member-results">
              {memberResults.map((m) => (
                <div key={m.uid} className="member-row" onClick={() => togglePick(m)} data-testid={`group-member-result-${m.uid}`}>
                  <Avatar name={m.display_name} url={m.avatar_url} style={{ width: 28, height: 28, fontSize: 10 }} />
                  <span style={{ flex: 1 }}>{m.display_name}</span>
                  {groupPicks.some((x) => x.uid === m.uid) && <span style={{ color: "var(--teal)", fontWeight: 700 }}>✓</span>}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setNewGroupOpen(false)}>Cancel</button>
              <button className="btn btn-teal btn-sm" onClick={createGroup} data-testid="group-create-submit">Create group</button>
            </div>
          </div>
        </div>
      )}

      {addMemberOpen && (
        <div className="modal-overlay" onClick={() => setAddMemberOpen(false)} data-testid="add-member-modal">
          <div className="modal-card" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="link-btn" style={{ position: "absolute", top: 14, right: 18, fontSize: 20 }} onClick={() => setAddMemberOpen(false)} aria-label="Close">×</button>
            <h3>Add a member</h3>
            <input type="text" style={{ marginTop: 14 }} placeholder="Search members..." value={memberQuery} onChange={(e) => searchMembers(e.target.value)} data-testid="add-member-search" />
            <div className="member-results">
              {memberResults.map((m) => (
                <div key={m.uid} className="member-row" onClick={() => addMemberToGroup(m.uid)} data-testid={`add-member-result-${m.uid}`}>
                  <Avatar name={m.display_name} url={m.avatar_url} style={{ width: 28, height: 28, fontSize: 10 }} />
                  <span style={{ flex: 1 }}>{m.display_name}</span>
                  <span className="link-btn">Add</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {dirOpen && (
        <div className="modal-overlay" onClick={() => setDirOpen(false)} data-testid="members-directory-modal">
          <div className="modal-card dir-card" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="link-btn" style={{ position: "absolute", top: 14, right: 18, fontSize: 20 }} onClick={() => setDirOpen(false)} aria-label="Close">×</button>
            <h3>Members</h3>
            <input type="text" style={{ marginTop: 14 }} placeholder="Search members by name..." value={dirQuery} onChange={(e) => searchDirectory(e.target.value)} data-testid="directory-search" />
            <div className="dir-filters" data-testid="directory-filters">
              {["All", "Top Contributor", "Active Contributor", "Conversation Starter", "New Member", "Team"].map((b) => (
                <button key={b} className="dir-filter" data-active={dirBadge === b} onClick={() => filterDirectory(b)} data-testid={`directory-filter-${b.replace(/ /g, "-")}`}>{b}</button>
              ))}
            </div>
            <div className="dir-grid" data-testid="directory-list">
              {dirMembers.length === 0 ? (
                <p style={{ color: "var(--faint)", fontSize: 13, gridColumn: "1/-1" }}>No members found.</p>
              ) : (
                dirMembers.map((m) => (
                  <div key={m.uid} className="dir-member" data-testid={`directory-member-${m.uid}`}>
                    <Avatar name={m.display_name} url={m.avatar_url} style={{ width: 44, height: 44, fontSize: 15, background: "var(--ink)" }} onClick={() => { setDirOpen(false); openProfile(m.uid); }} />
                    <div className="dir-name">{m.display_name}</div>
                    <Badge label={m.badge} />
                    {m.bio && <p className="dir-bio">{m.bio}</p>}
                    <button className="btn btn-teal btn-sm" style={{ marginTop: 8 }} onClick={() => { setDirOpen(false); startDMWith(m.uid); }} data-testid={`directory-message-${m.uid}`}>Message</button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {profileUid && (
        <div className="modal-overlay" onClick={() => setProfileUid(null)} data-testid="profile-modal">
          <div className="modal-card" style={{ textAlign: "center", position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="link-btn" style={{ position: "absolute", top: 14, right: 18, fontSize: 20 }} onClick={() => setProfileUid(null)} aria-label="Close" data-testid="profile-close">×</button>
            {!profileData ? (
              <p style={{ color: "var(--ink-muted)" }}>Loading...</p>
            ) : profileData.error ? (
              <p style={{ color: "var(--ink-muted)" }}>Could not load this profile.</p>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                  <Avatar name={profileData.display_name} url={profileData.avatar_url} style={{ width: 72, height: 72, fontSize: 24, background: "var(--ink)" }} />
                </div>
                <h3 data-testid="profile-name">{profileData.display_name}</h3>
                <div style={{ marginTop: 8 }}><Badge label={profileData.badge} /></div>
                {profileData.bio && <p style={{ color: "var(--ink-muted)", marginTop: 12, fontSize: 14 }} data-testid="profile-bio">{profileData.bio}</p>}
                <p style={{ color: "var(--faint)", marginTop: 14, fontSize: 12.5 }}>
                  {profileData.post_count} post{profileData.post_count === 1 ? "" : "s"}
                  {profileData.joined ? ` \u00b7 Member since ${profileData.joined}` : ""}
                </p>
                {user && profileData.uid && profileData.uid !== user.uid && (
                  <button className="btn btn-teal btn-sm" style={{ marginTop: 18 }}
                    onClick={() => startDMWith(profileData.uid)} data-testid="profile-message-button">
                    Send a message
                  </button>
                )}
                {user && profileData.uid && profileData.uid !== user.uid && (
                  <div style={{ marginTop: 10 }}>
                    {blockedUids.has(profileData.uid)
                      ? <button className="btn btn-ghost btn-sm" onClick={() => unblockUser(profileData.uid)} data-testid="profile-unblock-button">Unblock</button>
                      : <button className="btn btn-ghost btn-sm" onClick={() => blockUser(profileData.uid)} data-testid="profile-block-button">Block</button>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {reportTarget && (
        <div className="modal-overlay" style={{ zIndex: 240 }} onClick={() => setReportTarget(null)} data-testid="report-modal">
          <div className="modal-card" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="link-btn" style={{ position: "absolute", top: 14, right: 18, fontSize: 20 }} onClick={() => setReportTarget(null)} aria-label="Close" data-testid="report-close">×</button>
            <h3>Report this {reportTarget.label}</h3>
            <p style={{ color: "var(--ink-muted)", fontSize: 13, marginTop: 6 }}>
              Tell us what is wrong. Our team reviews every report.
            </p>
            <label className="field-label">Reason</label>
            <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="report-reason"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid var(--line-strong)", background: "var(--soft-fill)", color: "var(--ink)", fontFamily: "'Manrope',sans-serif" }}>
              {REPORT_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="field-label">Add a note (optional)</label>
            <textarea value={reportNote} onChange={(e) => setReportNote(e.target.value)} maxLength={1000} rows={3} data-testid="report-note"
              placeholder="Anything that helps us understand"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid var(--line-strong)", background: "var(--soft-fill)", color: "var(--ink)", fontFamily: "'Manrope',sans-serif", resize: "vertical" }} />
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setReportTarget(null)}>Cancel</button>
              <button className="btn btn-teal btn-sm" disabled={reportBusy} onClick={submitReport} data-testid="report-submit">
                {reportBusy ? "Sending..." : "Submit report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="modal-overlay" onClick={() => setEditOpen(false)} data-testid="edit-profile-modal">
          <div className="modal-card" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="link-btn" style={{ position: "absolute", top: 14, right: 18, fontSize: 20 }} onClick={() => setEditOpen(false)} aria-label="Close">×</button>
            <h3>Edit your profile</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 16 }}>
              <Avatar name={editForm.display_name || meName} url={editAvatar} style={{ width: 56, height: 56, fontSize: 18, background: "var(--ink)" }} />
              <label className="img-btn" style={{ cursor: "pointer" }}>
                Change photo
                <input type="file" accept="image/*" onChange={onAvatarPick} style={{ display: "none" }} data-testid="avatar-input" />
              </label>
            </div>
            <label className="field-label" htmlFor="edit-name">Display name</label>
            <input id="edit-name" type="text" maxLength={40} value={editForm.display_name}
              onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))} data-testid="edit-name-input" />
            <label className="field-label" htmlFor="edit-bio">Short bio</label>
            <textarea id="edit-bio" rows={3} maxLength={280} placeholder="How you use your Command Center, or anything you want other members to know."
              value={editForm.bio} onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))} data-testid="edit-bio-input" />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditOpen(false)}>Cancel</button>
              <button className="btn btn-teal btn-sm" onClick={saveProfile} disabled={savingProfile} data-testid="edit-save-button">
                {savingProfile ? "Saving..." : "Save profile"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <SiteFooter />
    <div style={{ height: "64px" }} aria-hidden="true" />
    <MobileTabBar active="community" />
    </>
  );
}
